import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  signal,
  effect,
  untracked,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { ApiService } from '@core/http/http.service';
import { ToasterService } from '@shared/services/toaster.service';
import { APP_CONSTANTS } from '@constants';
import { apiEndPoints } from '@apiEndPoints';
import {
  Subject,
  takeUntil,
  finalize,
  debounceTime,
  fromEvent,
  retry,
  catchError,
  of,
} from 'rxjs';
import { ChatService } from '@shared/services/chat.service';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog.component';

/**
 * Represents a menu item in the sidebar navigation
 */
interface MenuItem {
  /** Icon class for the menu item */
  icon: string;
  /** Display label for the menu item */
  label: string;
  /** Router path for navigation */
  route: string;
  /** Optional badge text or count */
  badge?: string | null;
  /** Optional description for tooltips */
  description?: string;
  /** Allowed user roles that can see this menu item */
  roles?: string[];
}

/**
 * Represents a single chat message
 */
interface ChatMessage {
  /** Message sender role */
  role: 'user' | 'assistant';
  /** Message content */
  content: string;
  /** Unique message identifier */
  _id: string;
  /** Message timestamp */
  timestamp: string;
}

/**
 * Represents a complete chat conversation
 */
interface ChatConversation {
  /** Unique conversation identifier */
  _id: string;
  /** Conversation title */
  title: string;
  /** Array of messages in the conversation */
  messages: ChatMessage[];
  /** Last update timestamp */
  lastUpdated: string;
  /** Creation timestamp */
  createdAt: string;
}

/**
 * API response structure for chat history
 */
interface ChatHistoryResponse {
  /** Request success status */
  success: boolean;
  /** Response data */
  data: {
    /** Array of conversations */
    conversations: ChatConversation[];
    /** Pagination information */
    pagination: {
      /** Current page number */
      currentPage: number;
      /** Total number of pages */
      totalPages: number;
      /** Total number of conversations */
      totalConversations: number;
      /** Whether more pages are available */
      hasMore: boolean;
    };
  };
}

/**
 * Skeleton loading item configuration
 */
interface SkeletonItem {
  /** Width percentage for title skeleton */
  titleWidth: number;
  /** Width percentage for preview skeleton */
  previewWidth: number;
}

/**
 * Loading state configuration
 */
interface LoadingState {
  /** Initial loading state */
  initial: boolean;
  /** Refreshing state */
  refreshing: boolean;
  /** Loading more items state */
  loadingMore: boolean;
}

/**
 * Sidebar layout component that manages navigation, chat history, and user interactions
 *
 * Features:
 * - Collapsible sidebar with persistent state
 * - Role-based menu filtering
 * - Infinite scroll chat history
 * - Chat management (load, delete, create new)
 * - Responsive design with skeleton loading
 * - Delete confirmation dialog
 *
 * @example
 * <sidebar-layout></sidebar-layout>
 */
@Component({
  selector: 'sidebar-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, ConfirmationDialogComponent],
  templateUrl: './sidebar-layout.component.html',
  styleUrls: ['./sidebar-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarLayoutComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  // Constants
  private static readonly SCROLL_THRESHOLD = 200;
  private static readonly DEFAULT_PAGE_SIZE = 5;
  private static readonly SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';
  private static readonly SCROLL_DEBOUNCE_TIME = 100;
  private static readonly MAX_RETRY_ATTEMPTS = 3;
  private static readonly TITLE_MAX_LENGTH = 25;

  // ViewChild references
  @ViewChild('scrollContainer', { static: false })
  scrollContainer!: ElementRef<HTMLDivElement>;

  // Injected services
  private readonly destroy$ = new Subject<void>();
  private readonly apiService = inject(ApiService);
  private readonly toaster = inject(ToasterService);
  private readonly chatService = inject(ChatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  // Component state
  /** Whether the sidebar is collapsed */
  isCollapsed = false;

  /** Application version */
  readonly appVersion = '2.0.0';

  /** Current year for footer display */
  readonly currentYear = new Date().getFullYear();

  /** Current logged-in user role */
  userRole: string = 'user';

  // Chat history properties
  /** Array of chat conversations */
  chatHistories: ChatConversation[] = [];

  /** Currently selected chat ID */
  currentChatId: string | null = null;

  // Loading states using consolidated signal
  /** Consolidated loading state */
  loadingState = signal<LoadingState>({
    initial: false,
    refreshing: false,
    loadingMore: false,
  });

  private historyAutoRefreshEnabled = false;

  private readonly historyRefreshEffect = effect(
    () => {
      const refreshCounter = this.chatService.historyRefreshCounter();

      if (!this.historyAutoRefreshEnabled || refreshCounter === 0) {
        return;
      }

      const handled = untracked(() => this.applySignalDrivenHistoryUpdate());
      if (!handled) {
        this.fetchChatHistory(1, false);
      }
    },
    { allowSignalWrites: true }
  );

  // Delete confirmation properties
  /** Controls delete confirmation dialog visibility */
  showDeleteConfirmation = signal(false);

  /** ID of the chat pending deletion */
  chatToDelete: string | null = null;

  /** Title of the chat being deleted (for better UX) */
  chatToDeleteTitle: string = '';

  // Pagination state
  /** Current page number */
  currentPage = 1;

  /** Total number of pages */
  totalPages = 1;

  /** Whether more chats are available */
  hasMoreChats = false;

  /** Total number of conversations */
  totalConversations = 0;

  // Scroll management
  private isScrolling = false;

  // Cached menu items (optimization)
  /** Filtered menu items based on user role */
  visibleMenuItems: MenuItem[] = [];

  // Skeleton loading configurations
  /** Main skeleton items for initial loading */
  readonly skeletonItems: SkeletonItem[] = [
    { titleWidth: 85, previewWidth: 70 },
    { titleWidth: 60, previewWidth: 80 },
    { titleWidth: 90, previewWidth: 65 },
    { titleWidth: 75, previewWidth: 75 },
    { titleWidth: 95, previewWidth: 60 },
    { titleWidth: 70, previewWidth: 85 },
    { titleWidth: 80, previewWidth: 70 },
  ];

  /** Skeleton items for load more functionality */
  readonly loadMoreSkeletonItems: SkeletonItem[] = [
    { titleWidth: 75, previewWidth: 65 },
    { titleWidth: 85, previewWidth: 80 },
    { titleWidth: 65, previewWidth: 70 },
  ];

  // Full list of menu items
  private menuItems: MenuItem[] = [
    {
      icon: 'pi pi-plus',
      label: 'New Conversation',
      route: '/prompt',
      badge: 'New',
      description: 'Start a new conversation',
      roles: ['user'], // subadmin not added here
    },
    {
      icon: 'pi pi-users',
      label: 'Users',
      route: '/admin/users',
      description: 'Manage application users',
      roles: ['admin', 'subadmin'],
    },
    {
      icon: 'pi pi-id-card',
      label: 'Human Design',
      route: '/admin/human-design',
      description: 'View and analyze user Human Design profiles',
      roles: ['admin', 'subadmin'],
    },
    {
      icon: 'pi pi-book',
      label: 'Knowledge Base',
      route: '/admin/knowledge-base',
      description: 'Manage knowledge base content and articles',
      roles: ['admin'], // Only admin access
    },
    {
      icon: 'pi pi-sliders-h',
      label: 'General',
      route: '/admin/general',
      description: 'Access general tools and miscellaneous settings',
      roles: ['admin', 'subadmin'],
    },
    {
      icon: 'pi pi-user-plus',
      label: 'Create Subadmin',
      route: '/admin/create-subadmin',
      description: 'Create and manage subadmin accounts',
      roles: ['admin'], // subadmin not added here
    },
    {
      icon: 'pi pi-credit-card',
      label: 'Plan Management',
      route: '/admin/plans',
      description: 'Create and manage subscription plans',
      roles: ['admin'],
    },
    {
      icon: 'pi pi-money-bill',
      label: 'Transactions',
      route: '/admin/transactions',
      description: 'View payment transactions',
      roles: ['admin'],
    },
  ];

  constructor(private authService: AuthenticationService) {}

  /**
   * Component initialization
   * Loads sidebar state, user role, and initial chat history
   */
  ngOnInit(): void {
    this.loadSidebarState();
    this.loadUserRole();
    this.updateVisibleMenuItems();

    // Load chat history for users only
    this.historyAutoRefreshEnabled = this.userRole === 'user';
    if (this.historyAutoRefreshEnabled) {
      this.fetchChatHistory(1, true);
    }
  }

  /**
   * Setup scroll listener after view initialization
   */
  ngAfterViewInit(): void {
    this.setupScrollListener();
  }

  /**
   * Cleanup subscriptions and subjects
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads user role from authentication cookie
   * @private
   */
  private loadUserRole(): void {
    const userCookie = this.authService.getCookie(
      APP_CONSTANTS.COOKIE_NAMES.USER
    );

    if (userCookie) {
      try {
        const user = JSON.parse(userCookie);
        this.userRole = user.role || 'user';
      } catch (error) {
        console.error('Invalid user cookie:', error);
        this.userRole = 'user'; // fallback
      }
    }
  }

  /**
   * Updates the cached visible menu items based on user role
   * @private
   */
  private updateVisibleMenuItems(): void {
    this.visibleMenuItems = this.menuItems.filter(
      (item) => !item.roles || item.roles.includes(this.userRole)
    );
    this.cdr.markForCheck();
  }

  /**
   * Sets up infinite scroll listener with debouncing
   * @private
   */
  private setupScrollListener(): void {
    if (this.scrollContainer?.nativeElement) {
      fromEvent(this.scrollContainer.nativeElement, 'scroll')
        .pipe(
          debounceTime(SidebarLayoutComponent.SCROLL_DEBOUNCE_TIME),
          takeUntil(this.destroy$)
        )
        .subscribe((event) => this.onScroll(event));
    }
  }

  /**
   * Fetches chat history from API with improved error handling and retry logic
   * @param page - Page number to fetch (default: 1)
   * @param isInitial - Whether this is the initial load
   * @param limit - Number of items per page
   */
  fetchChatHistory(
    page: number = 1,
    isInitial: boolean = false,
    limit: number = SidebarLayoutComponent.DEFAULT_PAGE_SIZE
  ): void {
    if (this.userRole !== 'user') return;

    // Update loading state
    this.updateLoadingState(isInitial, page);

    this.apiService
      .get<{
        conversations: ChatConversation[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalConversations: number;
          hasMore: boolean;
        };
      }>(`${apiEndPoints.chatPrompt.history}?page=${page}&limit=${limit}`)
      .pipe(
        retry(SidebarLayoutComponent.MAX_RETRY_ATTEMPTS),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Failed to fetch chat history:', error);
          this.toaster?.showError(
            error.message || 'Failed to load chat history'
          );
          return of(null);
        }),
        finalize(() => {
          this.resetLoadingState();
          this.isScrolling = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.handleChatHistoryResponse(response, page);
          }
        },
      });
  }

  /**
   * Updates loading state based on operation type
   * @private
   */
  private updateLoadingState(isInitial: boolean, page: number): void {
    const currentState = this.loadingState();

    if (isInitial) {
      this.loadingState.set({ ...currentState, initial: true });
    } else if (page === 1) {
      this.loadingState.set({ ...currentState, refreshing: true });
    } else {
      this.loadingState.set({ ...currentState, loadingMore: true });
    }
  }

  /**
   * Resets all loading states
   * @private
   */
  private resetLoadingState(): void {
    this.loadingState.set({
      initial: false,
      refreshing: false,
      loadingMore: false,
    });
  }

  /**
   * Applies chat history updates from the active chat signal instead of refetching from the server.
   * Returns true when the update was handled locally, or false to fall back to an API refresh.
   */
  private applySignalDrivenHistoryUpdate(): boolean {
    const selectedChat = this.chatService.getSelectedChat();
    const chatId = selectedChat.id;

    if (!chatId) {
      return false;
    }

    const conversations = this.chatService.conversations();
    if (!Array.isArray(conversations) || conversations.length === 0) {
      return false;
    }

    const messages = this.buildMessagesFromConversations(conversations);
    if (messages.length === 0) {
      return false;
    }

    const lastMessage = messages[messages.length - 1];
    const derivedTitle =
      selectedChat.title || this.generateTitleFromConversations(conversations);
    const existingIndex = this.chatHistories.findIndex(
      (chat) => chat._id === chatId
    );

    if (existingIndex > -1) {
      const existing = this.chatHistories[existingIndex];
      const updatedConversation: ChatConversation = {
        ...existing,
        title: derivedTitle || existing.title,
        messages,
        lastUpdated: lastMessage?.timestamp || existing.lastUpdated,
      };

      const updatedHistories = [...this.chatHistories];
      updatedHistories[existingIndex] = updatedConversation;
      this.chatHistories = updatedHistories;
    } else {
      const createdAt =
        conversations[0]?.timestamp instanceof Date
          ? conversations[0].timestamp.toISOString()
          : new Date().toISOString();

      const newConversation: ChatConversation = {
        _id: chatId,
        title: derivedTitle || 'New Conversation',
        messages,
        lastUpdated: lastMessage?.timestamp || createdAt,
        createdAt,
      };

      this.chatHistories = [newConversation, ...this.chatHistories];
      this.totalConversations = this.chatHistories.length;
    }

    this.cdr.markForCheck();
    return true;
  }

  /**
   * Builds chat message history entries from the active chat conversations.
   */
  private buildMessagesFromConversations(conversations: any[]): ChatMessage[] {
    const messages: ChatMessage[] = [];

    conversations.forEach((conversation: any, index: number) => {
      const timestampValue =
        conversation.timestamp instanceof Date
          ? conversation.timestamp.toISOString()
          : new Date().toISOString();

      if (conversation.question) {
        messages.push({
          role: 'user',
          content: conversation.question,
          _id: `${conversation.id || index}-user`,
          timestamp: timestampValue,
        });
      }

      const answerContent = conversation.answer || conversation.displayedAnswer;
      if (answerContent) {
        messages.push({
          role: 'assistant',
          content: answerContent,
          _id: `${conversation.id || index}-assistant`,
          timestamp: timestampValue,
        });
      }
    });

    return messages;
  }

  /**
   * Generates a fallback title from the first conversation question.
   */
  private generateTitleFromConversations(conversations: any[]): string {
    const firstQuestion = conversations[0]?.question?.trim();

    if (!firstQuestion) {
      return 'Conversation';
    }

    return this.getTruncatedTitle(
      firstQuestion,
      SidebarLayoutComponent.TITLE_MAX_LENGTH
    );
  }

  /**
   * Handles successful chat history API response
   * @private
   */
  private handleChatHistoryResponse(response: any, page: number): void {
    const conversations = response.conversations;
    const pagination = response.pagination;

    if (page === 1) {
      this.chatHistories = conversations;
    } else {
      this.chatHistories = [...this.chatHistories, ...conversations];
    }

    this.currentPage = pagination.currentPage;
    this.totalPages = pagination.totalPages;
    this.hasMoreChats = pagination.hasMore;
    this.totalConversations = pagination.totalConversations;

    this.cdr.markForCheck();
  }

  /**
   * Handles scroll events for infinite scrolling
   * @param event - Scroll event from the container
   */
  onScroll(event: Event): void {
    if (
      this.isScrolling ||
      this.loadingState().loadingMore ||
      !this.hasMoreChats
    ) {
      return;
    }

    const target = event.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    if (scrollBottom <= SidebarLayoutComponent.SCROLL_THRESHOLD) {
      this.loadMoreChats();
    }
  }

  /**
   * Loads more chat history via infinite scroll
   * @private
   */
  private loadMoreChats(): void {
    if (
      this.hasMoreChats &&
      !this.loadingState().loadingMore &&
      !this.isScrolling &&
      this.currentPage < this.totalPages
    ) {
      this.isScrolling = true;
      this.fetchChatHistory(this.currentPage + 1, false);
    }
  }

  /**
   * Loads more conversations via button click
   * Loads the next batch of conversations when the "Load More" button is clicked
   */
  loadMoreConversations(): void {
    if (
      this.hasMoreChats &&
      !this.loadingState().loadingMore &&
      !this.loadingState().refreshing &&
      this.currentPage < this.totalPages
    ) {
      this.fetchChatHistory(
        this.currentPage + 1,
        false,
        SidebarLayoutComponent.DEFAULT_PAGE_SIZE
      );
    }
  }

  /**
   * Calculates remaining conversations count for button display
   * @returns Number of remaining conversations to load
   */
  get remainingConversationsCount(): number {
    return Math.max(0, this.totalConversations - this.chatHistories.length);
  }

  /**
   * Refreshes chat history from the beginning
   */
  refreshChatHistory(): void {
    if (this.loadingState().refreshing || this.loadingState().loadingMore)
      return;

    this.currentPage = 1;
    this.fetchChatHistory(1, false);
  }

  /**
   * Toggles sidebar collapsed state and persists to localStorage
   */
  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
    this.saveSidebarState();
    this.cdr.markForCheck();
  }

  /**
   * Loads sidebar state from localStorage
   * @private
   */
  private loadSidebarState(): void {
    try {
      const savedState = localStorage.getItem(
        SidebarLayoutComponent.SIDEBAR_STORAGE_KEY
      );
      if (savedState) {
        this.isCollapsed = JSON.parse(savedState);
      }
    } catch (error) {
      console.warn('Failed to load sidebar state from localStorage:', error);
      this.isCollapsed = false;
    }
  }

  /**
   * Saves sidebar state to localStorage
   * @private
   */
  private saveSidebarState(): void {
    try {
      localStorage.setItem(
        SidebarLayoutComponent.SIDEBAR_STORAGE_KEY,
        JSON.stringify(this.isCollapsed)
      );
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error);
    }
  }

  /**
   * Updates badge count for a specific menu item
   * @param route - Menu item route to update
   * @param count - New badge count or null to remove badge
   */
  updateBadgeCount(route: string, count: string | null): void {
    const item = this.menuItems.find((item) => item.route === route);
    if (item) {
      item.badge = count;
      this.updateVisibleMenuItems();
    }
  }

  /**
   * Adds a new menu item to the sidebar
   * @param menuItem - Menu item to add
   */
  addMenuItem(menuItem: MenuItem): void {
    this.menuItems.push(menuItem);
    this.updateVisibleMenuItems();
  }

  /**
   * Removes a menu item by route
   * @param route - Route of the menu item to remove
   */
  removeMenuItem(route: string): void {
    this.menuItems = this.menuItems.filter((item) => item.route !== route);
    this.updateVisibleMenuItems();
  }

  /**
   * Gets the current sidebar collapsed state
   * @returns Current collapsed state
   */
  getSidebarState(): boolean {
    return this.isCollapsed;
  }

  /**
   * Gets menu items that have badges
   * @returns Array of menu items with badges
   */
  getActiveMenuItems(): MenuItem[] {
    return this.menuItems.filter((item) => item.badge !== null);
  }

  /**
   * Calculates total badge count across all menu items
   * @returns Sum of all numeric badges
   */
  getTotalBadgeCount(): number {
    return this.menuItems.reduce((total, item) => {
      if (item.badge && !isNaN(Number(item.badge))) {
        return total + Number(item.badge);
      }
      return total;
    }, 0);
  }

  /**
   * Updates description for a specific menu item
   * @param route - Menu item route to update
   * @param description - New description text
   */
  updateMenuItemDescription(route: string, description: string): void {
    const item = this.menuItems.find((item) => item.route === route);
    if (item) {
      item.description = description;
      this.updateVisibleMenuItems();
    }
  }

  /**
   * Reorders menu items by moving item from one index to another
   * @param fromIndex - Source index
   * @param toIndex - Target index
   */
  reorderMenuItems(fromIndex: number, toIndex: number): void {
    const item = this.menuItems.splice(fromIndex, 1)[0];
    this.menuItems.splice(toIndex, 0, item);
    this.updateVisibleMenuItems();
  }

  /**
   * Handles keyboard navigation for menu items
   * @param event - Keyboard event
   * @param route - Menu item route
   */
  onKeydown(event: KeyboardEvent, route: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Navigation logic can be implemented here
      // this.router.navigate([route]); // enable if router is injected
    }
  }

  /**
   * Checks if current user is admin
   * @returns True if user has admin role
   */
  get isAdmin(): boolean {
    return this.userRole === 'admin';
  }

  /**
   * Checks if current user is subadmin
   * @returns True if user has subadmin role
   */
  get isSubadmin(): boolean {
    return this.userRole === 'subadmin';
  }

  /**
   * Starts a new chat conversation
   * Resets current chat and refreshes history
   */
  startNewChat(): void {
    this.currentChatId = null;
    this.chatService.resetChat();
    this.refreshChatHistory();
  }

  /**
   * Loads a specific chat conversation
   * @param chatId - ID of the chat to load
   */
  loadChat(chatId: string): void {
    if (chatId && chatId !== this.currentChatId) {
      this.currentChatId = chatId;
      console.log('Loading chat:', chatId);
      this.chatService.setSelectedChat(chatId);
      
      // Navigate to chat page if not already there
      if (this.router.url !== '/prompt') {
        this.router.navigate(['/prompt']);
      }
      
      this.cdr.markForCheck();
    }
  }

  /**
   * Shows delete confirmation dialog
   * @param chatId - ID of the chat to delete
   * @param chatTitle - Title of the chat for better UX
   * @param event - Click event to stop propagation
   */
  showDeleteDialog(chatId: string, chatTitle: string, event: Event): void {
    event.stopPropagation();
    this.chatToDelete = chatId;
    this.chatToDeleteTitle = chatTitle;
    this.showDeleteConfirmation.set(true);
  }

  /**
   * Handles delete confirmation
   * Proceeds with chat deletion when user confirms
   */
  onDeleteConfirmed(): void {
    if (this.chatToDelete) {
      this.deleteChat(this.chatToDelete);
      this.resetDeleteDialog();
    }
  }

  /**
   * Handles delete cancellation
   * Resets dialog state when user cancels
   */
  onDeleteCancelled(): void {
    this.resetDeleteDialog();
  }

  /**
   * Handles dialog visibility changes
   * @param visible - New visibility state
   */
  onDialogVisibilityChanged(visible: boolean): void {
    this.showDeleteConfirmation.set(visible);
    if (!visible) {
      this.resetDeleteDialog();
    }
  }

  /**
   * Resets delete dialog state
   * @private
   */
  private resetDeleteDialog(): void {
    this.chatToDelete = null;
    this.chatToDeleteTitle = '';
    this.showDeleteConfirmation.set(false);
  }

  /**
   * Gets confirmation message with chat title for better UX
   * @returns Personalized confirmation message
   */
  get deleteConfirmationMessage(): string {
    const truncatedTitle = this.getTruncatedTitle(this.chatToDeleteTitle, 30);
    return `Are you sure you want to delete "${truncatedTitle}"? This action cannot be undone.`;
  }

  /**
   * Deletes a chat conversation with optimistic UI updates
   * @param chatId - ID of the chat to delete
   */
  deleteChat(chatId: string): void {
    if (!chatId) return;

    // Optimistically remove from UI
    const originalHistory = [...this.chatHistories];
    this.chatHistories = this.chatHistories.filter((c) => c._id !== chatId);
    this.totalConversations = Math.max(0, this.totalConversations - 1);

    if (this.currentChatId === chatId) {
      this.currentChatId =
        this.chatHistories.length > 0 ? this.chatHistories[0]._id : null;
    }

    this.cdr.markForCheck();
    this.deleteChatFromServer(chatId, originalHistory);
  }

  /**
   * Deletes chat from server with error handling and rollback
   * @private
   * @param chatId - ID of the chat to delete
   * @param originalHistory - Original chat history for rollback
   */
  private deleteChatFromServer(
    chatId: string,
    originalHistory: ChatConversation[]
  ): void {
    this.apiService
      .delete(`${apiEndPoints.chatPrompt.historyDelete}?id=${chatId}`)
      .pipe(
        retry(SidebarLayoutComponent.MAX_RETRY_ATTEMPTS),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Failed to delete chat:', error);
          this.toaster?.showError('Failed to delete chat');

          // Restore original state on error
          this.chatHistories = originalHistory;
          this.totalConversations = originalHistory.length;
          this.cdr.markForCheck();

          return of(null);
        })
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.toaster?.showSuccess('Chat deleted successfully');
            this.currentPage = 1;
            this.fetchChatHistory(1, true);
          }
        },
      });
  }

  /**
   * Formats date for chat display based on recency
   * @param dateString - ISO date string
   * @returns Formatted date string
   */
  getFormattedDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffInHours < 168) {
      // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }

  /**
   * Truncates title for display with ellipsis
   * @param title - Original title text
   * @param maxLength - Maximum length before truncation
   * @returns Truncated title with ellipsis if needed
   */
  getTruncatedTitle(
    title: string,
    maxLength: number = SidebarLayoutComponent.TITLE_MAX_LENGTH
  ): string {
    return title.length > maxLength
      ? title.substring(0, maxLength) + '...'
      : title;
  }

  // Computed properties for template use
  get isInitialLoading(): boolean {
    return this.loadingState().initial;
  }

  get isRefreshing(): boolean {
    return this.loadingState().refreshing;
  }

  get isLoadingMore(): boolean {
    return this.loadingState().loadingMore;
  }
}
